import { Avatar, Box, Group, Space, Stack, Text, useMantineTheme } from "@mantine/core";
import { EntityCardProps, ValuesObject } from "@mcurros2/microm";
import { IconAlertTriangle, IconAt, IconCake, IconCircleCheck, IconGenderNeutrois, IconId, IconInfoCircle, IconMilitaryRank, IconPhoneCall, IconUser } from "@tabler/icons-react";

export interface PersonalCardData extends ValuesObject {
    c_personal_id: string,
    vc_puestotrabajo: string,
    c_personallegajo_id: string,
    dt_fecha_alta: string,
    vc_numerodni: string,
    vc_numerocuil: string,
    vc_areapersonal: string,
    vc_nombre: string,
    vc_apellido: string,
    dt_nacimiento: string,
    c_fotofileprocess_id: string,
    vc_genero: string,
    vc_fotoguid: string,
    vc_telefono: string,
    vc_celular: string,
    vc_celular_trabajo: string,
    vc_telefono_alternativo: string,
    vc_email: string,
    vc_email_alternativo: string,
    f_latitud: number,
    f_longitud: number,
    vc_pais: string,
    vc_provincia: string,
    vc_ciudad: string,
    vc_calle: string,
    vc_altura: string,
    vc_piso: string,
    vc_departamento: string,
    vc_codigopostal: string,
    dt_fecha_baja: string,
    c_motivobaja_id: string,
    dt_licencia_desde: string,
    dt_licencia_hasta: string,
    c_estado_licencia_id: string,
    vc_username: string,
    vc_users_groups: string,
    i_cantidad_grupos: string
}

export function PersonalCard(props: EntityCardProps<ValuesObject>) {
    const {
        entity, record
    } = props;

    const data = record.data as PersonalCardData;

    const perfiles = data.i_cantidad_grupos === '1' ? ('acceso a ' + data.vc_users_groups) : data.vc_users_groups ? 'acceso a múltiples perfiles' : 'Sin permisos';

    const fotoURL = entity.API.client.getThumbnailURL(data.vc_fotoguid);

    const theme = useMantineTheme();
    const iconColor = theme.colorScheme === "dark" ? theme.colors.dark[3] : theme.colors.gray[5];
    const warningColor = theme.colorScheme === "dark" ? theme.colors.orange[5] : theme.colors.orange[7];
    const errorColor = theme.colorScheme === "dark" ? theme.colors.red[5] : theme.colors.red[7];
    const okColor = theme.colorScheme === "dark" ? theme.colors.green[5] : theme.colors.green[7];
    const activeColor = theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.colors.dark[7];

    const widthCard = "20rem";

    return (
        <Box mih="18rem" w={widthCard}>
            <Text fz="lg" align="center" fw={500} mb="xs" color={data.dt_fecha_baja ? 'dimmed' : activeColor} >{data.vc_apellido}, {data.vc_nombre}</Text>
            <Group noWrap align="flex-start">
                <Box>
                    <Avatar size="xl" src={fotoURL} radius="sm" />
                </Box>
                <Stack spacing={1}>
                    <Group noWrap spacing={10}>
                        <IconAt stroke={1.5} size="1rem" color={data.vc_email ? iconColor : warningColor} />
                        <Text fz="xs" c="dimmed">
                            {data.vc_email || "Sin Email"}
                        </Text>
                    </Group>
                    <Group noWrap spacing={10}>
                        <IconPhoneCall stroke={1.5} size="1rem" color={data.vc_celular_trabajo ? iconColor : warningColor} />
                        <Text fz="xs" c="dimmed">
                            {data.vc_celular_trabajo || "Sin celular de trabajo"}
                        </Text>
                    </Group>
                    <Group noWrap spacing={10}>
                        <IconId stroke={1.5} size="1rem" color={iconColor} />
                        <Text fz="xs" c="dimmed">
                            {data.vc_numerodni}
                        </Text>
                    </Group>
                    <Group noWrap spacing={10}>
                        <IconGenderNeutrois stroke={1.5} size="1rem" color={data.vc_genero ? iconColor : warningColor} />
                        <Text fz="xs" c="dimmed">
                            {data.vc_genero || "Sin género"}
                        </Text>
                    </Group>
                    <Group noWrap spacing={10}>
                        <IconCake stroke={1.5} size="1rem" color={data.dt_nacimiento ? iconColor : warningColor} />
                        <Text fz="xs" c="dimmed">
                            {data.dt_nacimiento || "Sin fecha de nacimiento"}
                        </Text>
                    </Group>
                </Stack>
            </Group>
            <Group mt="xs">
                <Group noWrap spacing={5}>
                    <IconMilitaryRank stroke={1.5} size="1rem" color={data.vc_puestotrabajo ? iconColor : warningColor} />
                    <Text fz="0.8rem" c="dimmed" fw={700}>
                        {data.vc_puestotrabajo || "Sin Puesto"}
                    </Text>
                </Group>
            </Group>
            <Group mt="xs">
                <Group noWrap spacing={5} maw={widthCard}>
                    <IconUser
                        stroke={1.5}
                        size="1rem"
                        style={{ minWidth: "1rem" }}
                        color={data.vc_users_groups ? okColor : data.vc_username ? warningColor : errorColor}
                    />
                    <Text fz="0.8rem" c="dimmed" fw={700} truncate>
                        {data.vc_username || "Sin acceso"}, {perfiles}
                    </Text>
                </Group>
            </Group>
            <Space mt="xs" />
            {data.dt_fecha_baja &&
                <Group noWrap spacing={10}>
                    <IconAlertTriangle stroke={1.5} size="1rem" color={errorColor} />
                    <Text fz="xs" c="dimmed">
                        BAJA - {data.dt_fecha_baja.substring(0, 10)} - {data.c_motivobaja_id}
                    </Text>
                </Group>
            }
            {!data.dt_fecha_baja &&
                <Group noWrap spacing={10}>
                    <IconCircleCheck stroke={1.5} size="1rem" color={okColor} />
                    <Text fz="xs" c="dimmed">
                        ACTIVO - {data.dt_fecha_alta.substring(0, 10)}
                    </Text>
                </Group>
            }
            {data.dt_licencia_desde && data.c_estado_licencia_id === "PROXIMA" &&
                <Group noWrap spacing={10}>
                    <IconInfoCircle stroke={1.5} size="1rem" color={warningColor} />
                    <Text fz="xs" c="dimmed">
                        PRÓXIMA LICENCIA - {data.dt_licencia_desde.substring(0, 10)} - {data.dt_licencia_hasta.substring(0, 10)}
                    </Text>
                </Group>
            }
            {data.dt_licencia_desde && data.c_estado_licencia_id !== "PROXIMA" &&
                <Group noWrap spacing={10}>
                    <IconAlertTriangle stroke={1.5} size="1rem" color={warningColor} />
                    <Text fz="xs" c="dimmed">
                        LICENCIA ACTIVA - {data.dt_licencia_desde.substring(0, 10)} - {data.dt_licencia_hasta.substring(0, 10)}
                    </Text>
                </Group>
            }
        </Box>
    )
}